import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import usePlacesAutocomplete, { getGeocode } from 'use-places-autocomplete'; 
import propertyService from '../services/propertyService';
import { Button } from './ui/button';
import { Loader2, X, MapPin } from 'lucide-react';

// Simple input component
const Input = React.forwardRef<
  HTMLInputElement,
  React.InputHTMLAttributes<HTMLInputElement>
>(({ className, ...props }, ref) => {
  return (
    <input
      ref={ref}
      className={`flex h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm ring-offset-white file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-gray-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
      {...props}
    />
  )
})
Input.displayName = "Input"

interface AddPropertyAutoCompleteProps {
  onClose: () => void;
  onSuccess: () => void;
}

const AddPropertyAutoComplete: React.FC<AddPropertyAutoCompleteProps> = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [addressParts, setAddressParts] = useState({ line1: '', city: '', state: '', zip: '' });

  const navigate = useNavigate();

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete();

  const handleSelect = async (address: string) => {
    setValue(address, false);
    clearSuggestions();

    try {
      const geocodeResults = await getGeocode({ address });
      if (geocodeResults.length > 0) {
        const addressComponents = geocodeResults[0].address_components;

        const line1 = addressComponents.find((c :any) =>
          c.types.includes('street_number')
        )?.long_name;
        const route = addressComponents.find((c:any) => c.types.includes('route'))?.long_name;
        const city = addressComponents.find((c:any) =>
          c.types.includes('locality')
        )?.long_name;
        const state = addressComponents.find((c:any) =>
          c.types.includes('administrative_area_level_1')
        )?.short_name;
        const zip = addressComponents.find((c:any) => c.types.includes('postal_code'))?.long_name;

        setAddressParts({
          line1: `${line1 || ''} ${route || ''}`.trim(),
          city: city || '',
          state: state || '',
          zip: zip || '',
        });
      }
    } catch (error) {
      console.error('Error fetching address details:', error);
    }
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!addressParts.line1) return;

    setLoading(true);
    setLoadingMessage('Creating property...');

    try {
      const propertyId = await propertyService.createProperty(addressParts.line1, addressParts.city, addressParts.state, addressParts.zip);
      if (!propertyId) {
        throw new Error('Failed to create property');
      }
      setLoadingMessage('Redirecting...');
      onSuccess();
      // Immediately navigate to the property page - data enrichment happens in background
      navigate(`/property/${propertyId}`);
    } catch (error) {
      console.error('Error creating property:', error);
      setLoadingMessage('An error occurred while creating the property. Please try again.');
    } finally {
      setLoading(false);
      onClose();
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Add New Property</h2>
        <Button
          variant="ghost"
          onClick={onClose}
          className="rounded-full h-8 w-8 p-0 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <label className="text-sm font-medium">
            Property Address
          </label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <Input
              placeholder="Start typing an address..."
              value={value}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
              disabled={!ready}
              className="pl-10"
            />
          </div>
          
          {/* Address Suggestions */}
          {status === 'OK' && (
            <div className="border rounded-md shadow-sm overflow-hidden">
              {data.map((suggestion: any) => (
                <div
                  key={suggestion.place_id}
                  onClick={() => handleSelect(suggestion.description)}
                  className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b last:border-b-0"
                >
                  <div className="flex items-start">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-sm">{suggestion.description}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Selected address details */}
        {addressParts.line1 && (
          <div className="p-3 bg-blue-50 rounded-md">
            <p className="font-medium text-sm">Selected Address:</p>
            <p className="text-sm text-gray-600">
              {addressParts.line1}, {addressParts.city}, {addressParts.state} {addressParts.zip}
            </p>
          </div>
        )}
        
        <div className="pt-2 flex gap-3 justify-end">
          <Button 
            type="button" 
            variant="outline" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          <Button 
            type="submit" 
            disabled={!addressParts.line1 || loading}
            className="bg-fxer-blue hover:opacity-90"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                <span>{loadingMessage || 'Creating property...'}</span>
              </>
            ) : (
              <span>Add Property</span>
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default AddPropertyAutoComplete;